<script setup>
import { computed, inject, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { DateTime, Settings } from 'luxon';
import { PrimaryButton, SecondaryButton, DateRangePicker, useSnackbarStore } from '@es-bill/es-bill-common-components';
import { useCommunitiesStore } from "@/stores/communitiesStore";
import { useMeteringPointMembershipsStore } from "@/stores/meteringPointMembershipsStore";
import edaTools from "@/lib/es-bill-shared-js/misc/edaTools";
import { resolveComboboxItem, isComboboxItem } from "@/utils/comboboxUtils";
import { fetchEnergyDataHistory, buildEnergyDataHistoryReport, fmtKwh } from "@/services/accountabilityService";

const $log = inject('log');
const { t } = useI18n();
Settings.defaultZone = 'Europe/Vienna';

const communitiesStore = useCommunitiesStore();
const mpmStore = useMeteringPointMembershipsStore();
const snackbarStore = useSnackbarStore();

const communityAvailable = computed(() => communitiesStore.allCommunities.map(c => ({
  title: c.name,
  props: { subtitle: c.ecid },
  ecn: c.ecn,
  ecid: c.ecid,
})));

const selectedCommunity = ref(null);
const selectedMeteringPoint = ref(null);
const dateRange = ref([]);
const loading = ref(false);
const history = ref(null);

const meteringPointsAvailable = computed(() => {
  if (!isComboboxItem(selectedCommunity.value)) return [];
  return mpmStore.allMeteringPointMemberships
    .filter(mpm => mpm.ecn === selectedCommunity.value.ecn
      && mpm.ecid === selectedCommunity.value.ecid)
    .map(mpm => ({
      title: edaTools.shortenIdentifiersLong(mpm.meteringPoint),
      props: { subtitle: `${mpm.nameFriendly || ''} (${mpm.membershipStatus})` },
      meteringPoint: mpm.meteringPoint,
    }));
});

watch(selectedCommunity, value => {
  selectedCommunity.value = resolveComboboxItem(value, communityAvailable.value, ['ecid', 'title']);
});
watch(selectedMeteringPoint, value => {
  selectedMeteringPoint.value = resolveComboboxItem(value, meteringPointsAvailable.value, ['meteringPoint', 'title']);
});

const isoRange = computed(() => {
  if (!dateRange.value || dateRange.value.length === 0) return null;
  const days = dateRange.value
    .map(d => DateTime.fromJSDate(new Date(d)).toISODate())
    .sort();
  return { from: days[0], to: days[days.length - 1] };
});

const requestReady = computed(() =>
  isComboboxItem(selectedCommunity.value) && isComboboxItem(selectedMeteringPoint.value) && isoRange.value);

const reportText = computed(() =>
  history.value ? buildEnergyDataHistoryReport(history.value) : '');

const messageHeader = computed(() => [
  { title: t('energyDataHistory.arrival'), key: 'arrivalDate' },
  { title: 'MessageId', key: 'messageId' },
  { title: 'ConversationId', key: 'conversationId' },
  { title: t('energyDataHistory.dataRange'), key: 'dataRange' },
  { title: t('energyDataHistory.processed'), key: 'dateProcessed' },
]);

async function loadHistory() {
  loading.value = true;
  history.value = null;
  try {
    history.value = await fetchEnergyDataHistory(
      selectedCommunity.value.ecn,
      selectedCommunity.value.ecid,
      selectedMeteringPoint.value.meteringPoint,
      isoRange.value.from,
      isoRange.value.to,
    );
  } catch (error) {
    $log.error('energy data history failed', error);
    snackbarStore.showSnackbar(t('energyDataHistory.loadFailed'), '', 'Error', 15000, []);
  } finally {
    loading.value = false;
  }
}

function copyReport() {
  navigator.clipboard.writeText(reportText.value);
  snackbarStore.showSnackbar(t('energyDataHistory.copied'), '', 'Success', 4000, []);
}

const formatDateTimeShort = (value) =>
  value ? new Date(value).toISOString().replace('T', ' ').slice(0, 16) : '—';
const formatDay = (value) => value ? String(value).slice(0, 10) : '—';
</script>

<template>
  <v-container class="containerBase pa-0">
    <v-container class="containerStandard">
      <p class="textHeadline">{{ t('energyDataHistory.title') }}</p>
      <p class="mb-4">{{ t('energyDataHistory.description') }}</p>

      <v-combobox
        max-width="428px" bg-color="white"
        :placeholder="t('community')"
        v-model="selectedCommunity" :items="communityAvailable"
        :filter-keys="['title', 'value']" density="comfortable" variant="outlined"
        item-value="ecid" hide-details clearable class="mb-4"
      />
      <v-combobox
        max-width="428px" bg-color="white"
        :placeholder="t('meteringPointNumber')"
        v-model="selectedMeteringPoint" :items="meteringPointsAvailable"
        :filter-keys="['title', 'value']" density="comfortable" variant="outlined"
        item-value="meteringPoint" hide-details clearable
        :disabled="!isComboboxItem(selectedCommunity)" class="mb-4"
      />
      <DateRangePicker
        class="mb-4"
        style="max-width: 428px"
        :dateRange="dateRange"
        :placeholder="t('dateRange')"
        @update:dateRange="(val) => dateRange = val"
      />

      <p v-if="isoRange" class="mb-4">
        {{ t('dateRange') }}: {{ isoRange.from }} — {{ isoRange.to }}
      </p>

      <PrimaryButton
        :disabled="!requestReady || loading" :loading="loading"
        @click="loadHistory" :text="t('energyDataHistory.load')" class="mb-6"
      />

      <template v-if="history">
        <p class="font-weight-bold mt-4">
          {{ t('energyDataHistory.messages') }} ({{ history.messages.length }})
        </p>
        <v-data-table density="compact" :headers="messageHeader"
          :items="history.messages" :items-per-page="25">
          <template v-slot:[`item.arrivalDate`]="{ value }">{{ formatDateTimeShort(value) }}</template>
          <template v-slot:[`item.dataRange`]="{ item }">
            {{ formatDay(item.dataRangeFrom) }} — {{ formatDay(item.dataRangeTo) }}
          </template>
          <template v-slot:[`item.dateProcessed`]="{ value }">
            <span :class="{'text-error font-weight-bold': !value}">
              {{ value ? formatDateTimeShort(value) : t('energyDataHistory.missing') }}
            </span>
          </template>
        </v-data-table>

        <p class="font-weight-bold mt-6">{{ t('energyDataHistory.updatesPerDay') }}</p>
        <p v-for="u in history.updatesPerDay" :key="u.date" class="text-body-2">
          {{ u.date }}: {{ u.count }}
        </p>

        <p class="font-weight-bold mt-6">{{ t('energyDataHistory.sums') }}</p>
        <p v-for="s in history.sumsByMeterCode" :key="s.meterCode" class="text-body-2">
          {{ s.meterCode }}: {{ fmtKwh(s.energySumkWh) }} kWh ({{ s.meteringMethod }})
        </p>

        <div class="mt-6">
          <SecondaryButton @click="copyReport" :text="t('energyDataHistory.copyReport')" />
          <pre class="report-block mt-2">{{ reportText }}</pre>
        </div>
      </template>
    </v-container>
  </v-container>
</template>

<style scoped>
.report-block {
  background: #f5f5f5;
  padding: 12px;
  font-size: 12px;
  overflow-x: auto;
  border-radius: 4px;
}
</style>
